<template>
  <div class="cases-container" ref="listRef">
    <SeoHeading :text="t('SEO_SECTIONS.CASES.h1')" />

    <CasesSkeletonLoader v-if="!hasVisibleCaseContent && !initialDataError" />

    <section v-else-if="!hasVisibleCaseContent" class="cases-load-error" role="alert">
      <div class="cases-load-error__icon" aria-hidden="true">
        <i class="fa-solid fa-cloud-arrow-down"></i>
      </div>
      <div class="cases-load-error__copy">
        <h2>{{ t('CASES_PAGE.loadError.title') }}</h2>
        <p>{{ t('CASES_PAGE.loadError.description') }}</p>
      </div>
      <Button
        variant="primary"
        size="lg"
        :loading="globalLoading"
        @click="app.fetchInitialData(false)"
      >
        <template #icon-left>
          <i class="fa-solid fa-rotate-right"></i>
        </template>
        {{ t('CASES_PAGE.loadError.retry') }}
      </Button>
    </section>

    <template v-if="hasVisibleCaseContent">
      <DiscountedCasesShowcase
        v-if="discountedShowcaseCases.length && !displayCaseCategories.length"
        :cases="discountedShowcaseCases"
      />

      <template v-for="(category, index) of displayCaseCategories" :key="category.name">
        <CasesCategory :title="category.name" :cases="getVisibleCasesByCategory(category.id)" />

        <DiscountedCasesShowcase
          v-if="index === 0 && discountedShowcaseCases.length"
          :cases="discountedShowcaseCases"
        />
      </template>
    </template>

    <!-- SEO Description Section -->
    <SeoDescription
      :title="t('SEO_SECTIONS.CASES.title')"
      :description="t('SEO_SECTIONS.CASES.description')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/store/app'
import { useCasesStore } from '@/store/cases'
import { storeToRefs } from 'pinia'
import type { Case } from '@/services/models/models'
// Components
import CasesCategory from '@/components/CasesCategory.vue'
import CasesSkeletonLoader from '@/components/SkeletonLoader/CasesSkeletonLoader.vue'
import DiscountedCasesShowcase from './components/DiscountedCasesShowcase.vue'
import SeoDescription from '@/components/SeoDescription.vue'
import SeoHeading from '@/components/SeoHeading.vue'
import Button from '@/components/Common/Button/Button.vue'
import { trackCaseListView } from '@/utils/gtagEvent'
import { useSeoMeta, SEO_PAGE } from '@/composables/useSeoMeta'

const { t } = useI18n()

const app = useAppStore()
const casesStore = useCasesStore()

// States
const { initialDataError, globalLoading } = storeToRefs(app)
const { caseCategories, prepareFilteredCases } = storeToRefs(casesStore)

const isDiscountedCase = (caseItem: Case) => (caseItem.discountPercent ?? 0) > 0

const discountedCases = computed(() => prepareFilteredCases.value.filter(isDiscountedCase))
const discountedShowcaseCases = computed(() => discountedCases.value.slice(0, 6))

const getVisibleCasesByCategory = (categoryId: Case['caseCategory']) => {
  const matchingCases = prepareFilteredCases.value.filter(
    (caseItem) => caseItem.caseCategory === categoryId,
  )

  if (!discountedCases.value.length) return matchingCases

  return matchingCases.filter((caseItem) => !isDiscountedCase(caseItem))
}

const displayCaseCategories = computed(() =>
  caseCategories.value.filter((category) => getVisibleCasesByCategory(category.id).length > 0),
)

const hasVisibleCaseContent = computed(
  () => displayCaseCategories.value.length > 0 || discountedShowcaseCases.value.length > 0,
)

const listRef = ref<HTMLElement | null>(null)
let caseListObserver: IntersectionObserver | null = null
let hasTrackedCaseList = false

useSeoMeta(SEO_PAGE.CASES)

onMounted(() => {
  app.setShowBanner(true)
  app.setShowFilters(true)
  app.setShowDrops(true)

  nextTick(() => {
    if (hasTrackedCaseList) return
    const element = listRef.value
    if (!element) {
      trackCaseListView({ list_id: 'homepage' })
      hasTrackedCaseList = true
      return
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      trackCaseListView({ list_id: 'homepage' })
      hasTrackedCaseList = true
      return
    }

    caseListObserver = new IntersectionObserver(
      (entries) => {
        if (hasTrackedCaseList) return
        const [entry] = entries
        if (entry?.intersectionRatio >= 0.5) {
          trackCaseListView({ list_id: 'homepage' })
          hasTrackedCaseList = true
          caseListObserver?.disconnect()
        }
      },
      { threshold: 0.5 },
    )

    caseListObserver.observe(element)
  })
})

onUnmounted(() => {
  app.setShowBanner(false)
  app.setShowFilters(false)
  app.setShowDrops(false)
  caseListObserver?.disconnect()
  caseListObserver = null
})
</script>

<style scoped lang="scss">
.cases-container {
  @include page-stack($space-xl);
  margin-top: $space-md;
  padding-bottom: 4rem;
}

.cases-load-error {
  @include surface-card($space-xl, $radius-lg);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: $space-lg;
  min-height: 180px;

  @include mobile {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
}

.cases-load-error__icon {
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: $radius-md;
  color: $accent-yellow;
  background: rgba($accent-yellow, 0.1);
  font-size: 1.5rem;
}

.cases-load-error__copy {
  h2,
  p {
    margin: 0;
  }

  h2 {
    color: $text-light-200;
  }

  p {
    margin-top: $space-xs;
    color: $text-dim;
  }
}
</style>
